// TanStack Query hooks for the worker API and the recorded fallback files. Every query reports to the
// api state store from inside its query function, so the ".api-state" text follows the polling.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, apiOn, get, isNotFound, offError } from './client';
import { fail, ok, setRecordedRange } from './state';
import type { BasisPayload, CensusPayload, ChartPayload, HookPayload, LatestPayload, SessionPayload } from './types';
import { asSnapshot, CHART_URL, SNAPSHOT_URL, type Snapshot } from '../lib/snapshot';

export const API_EVERY_MS = 30000;
export const BASIS_EVERY_MS = 120000;
export const CHART_EVERY_MS = 120000;
export const HOOK_EVERY_MS = 60000;
/** How long a /session answer counts as current. */
export const SESSION_MAX_AGE_MS = 600000;

const retryNever = { retry: false, refetchOnWindowFocus: false } as const;

/** Network errors, timeouts and 5xx are worth retrying; 4xx, ?api=off and malformed answers are final. */
export function retryable(e: unknown): boolean {
  if (e instanceof ApiError) return !e.off && (e.timeout || e.status === null || e.status >= 500);
  return e instanceof TypeError; // fetch() rejects with a TypeError on a network failure
}

/** Delay before retry n (0-based): 2 s, 6 s, 12 s, so three retries span about 20 s. */
export const retryDelayMs = (n: number): number => Math.min(2000 * 3 ** n, 12000);

/**
 * Worker API queries ride out short outages (Postgres restarts of 25 to 47 s): three retries with backoff,
 * and the last good answer stays on screen (TanStack keeps data on a failed refetch; gcTime keeps it cached).
 */
function resilient(everyMs: number) {
  return {
    retry: (failures: number, e: Error) => failures < 3 && retryable(e),
    retryDelay: retryDelayMs,
    staleTime: everyMs,
    gcTime: 30 * 60000,
    refetchOnWindowFocus: false,
  } as const;
}

async function fetchStatic<T>(url: string, check: (v: unknown) => T): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return check((await r.json()) as unknown);
}

export function useSnapshot(): UseQueryResult<Snapshot> {
  return useQuery({
    queryKey: ['snapshot'],
    queryFn: async () => {
      const snap = await fetchStatic(SNAPSHOT_URL, asSnapshot);
      setRecordedRange(snap.meta.recorded_from, snap.meta.recorded_to);
      return snap;
    },
    staleTime: Infinity,
    ...retryNever,
  });
}

export function useStaticChart(): UseQueryResult<ChartPayload> {
  return useQuery({
    queryKey: ['chart', 'static'],
    queryFn: () => fetchStatic(CHART_URL, (v) => v as ChartPayload),
    staleTime: Infinity,
    ...retryNever,
  });
}

export function useLatest(): UseQueryResult<LatestPayload> {
  return useQuery({
    queryKey: ['latest'],
    queryFn: async () => {
      if (!apiOn()) { const e = offError(); fail('latest', e); throw e; }
      try {
        const p = await get<LatestPayload>('/api/data/latest');
        if (typeof p.symbols !== 'object') throw new Error('malformed /latest answer');
        ok('latest', p);
        return p;
      } catch (e) {
        fail('latest', e);
        throw e;
      }
    },
    refetchInterval: API_EVERY_MS,
    ...resilient(API_EVERY_MS),
  });
}

/** /session answers carry the time they were received, so a stale answer can be ignored. */
export interface SessionAnswer { payload: SessionPayload; at: number }

export function useSession(): UseQueryResult<SessionAnswer> {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      if (!apiOn()) throw offError();
      try {
        const p = await get<SessionPayload>('/api/data/session');
        if (typeof p.state !== 'string') throw new Error('malformed /session answer');
        ok('session');
        return { payload: p, at: Date.now() };
      } catch (e) {
        fail('session', e);
        throw e;
      }
    },
    refetchInterval: API_EVERY_MS,
    ...resilient(API_EVERY_MS),
  });
}

export function useBasis(): UseQueryResult<BasisPayload> {
  return useQuery({
    queryKey: ['basis'],
    queryFn: async () => {
      try {
        const p = await get<BasisPayload>('/api/data/basis');
        ok('basis');
        return p;
      } catch (e) {
        fail('basis', e);
        throw e;
      }
    },
    enabled: apiOn(),
    refetchInterval: BASIS_EVERY_MS,
    ...resilient(BASIS_EVERY_MS),
  });
}

export function useApiChart(hours = 72): UseQueryResult<ChartPayload> {
  return useQuery({
    queryKey: ['chart', 'api', hours],
    queryFn: async () => {
      try {
        const p = await get<ChartPayload>(`/api/data/chart?hours=${hours}`);
        if (typeof p.symbols !== 'object') throw new Error('malformed /chart answer');
        ok('chart', p.meta);
        return p;
      } catch (e) {
        fail('chart', e);
        throw e;
      }
    },
    enabled: apiOn(),
    refetchInterval: CHART_EVERY_MS,
    ...resilient(CHART_EVERY_MS),
  });
}

/** /hook: the payload, or null when the worker has no readout yet (404). Other failures reject. */
export function useHookReadout(): UseQueryResult<HookPayload | null> {
  return useQuery({
    queryKey: ['hook'],
    queryFn: async () => {
      try {
        const p = await get<HookPayload>('/api/data/hook');
        ok('hook');
        return p;
      } catch (e) {
        if (isNotFound(e)) return null;
        fail('hook', e);
        throw e;
      }
    },
    enabled: apiOn(),
    refetchInterval: HOOK_EVERY_MS,
    ...resilient(HOOK_EVERY_MS),
  });
}

/** /census at the cadence of the hero readouts; off with the API, so the counter reads n/a. */
export function useCensus(): UseQueryResult<CensusPayload> {
  return useQuery({
    queryKey: ['census'],
    queryFn: async () => {
      try {
        const p = await get<CensusPayload>('/api/data/census');
        if (typeof p.tokens !== 'object' || typeof p.pools !== 'object') throw new Error('malformed /census answer');
        ok('census');
        return p;
      } catch (e) {
        fail('census', e);
        throw e;
      }
    },
    enabled: apiOn(),
    refetchInterval: API_EVERY_MS,
    ...resilient(API_EVERY_MS),
  });
}
